import { State } from 'redux/initialState';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import { store } from 'redux/store';
import { SocietyEquityMarketStatus } from 'types/Society';

import actionTypes from 'constants/actionTypes';
import menuTypes from 'constants/menuTypes';
import { formatDateToInput } from 'constants/formats';

import { useTranslate } from 'hooks/useTranslate';
import { useGetActualSociety } from 'hooks/useGetActualSociety';

import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import PublishTradingOfferModal from 'modules/trading/modals/PublishTradingOffer';
import { useGetPartnerOptions } from 'modules/partners/hooks/useGetPartnerOptions';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';
import { useGetHoldingClassOptions } from 'modules/partners/hooks/useGetHoldingClassesOption';
import { TradingOffer, TradingOfferType } from 'types/TradingOffer';
import { PublishTradingOfferModalProvider } from 'modules/trading/modals/PublishTradingOffer/context/PublishTradingOfferModalContext';
import { mapPartnerOptionToPartialPartner } from 'modules/trading/modals/PublishTradingOffer/utils/buildInitialPublisherPartner';

import errorSwal from 'utils/errorSwal';

import { getTradingOffer } from 'modules/trading/api/getTradingOffer';
import { addTradingOfferPageInitValue } from '../constants/addTradingOfferPageInitValue';
import { AddTradingOfferPageContextType } from '../types/AddTradingOfferPageContextType';

import { useGetPrices } from '../hooks/useGetPrices';
import { useGetReservedShares } from '../hooks/useGetReservedShares';
import { useGetMaxSharesToTransfer } from '../hooks/useGetMaxSharesToTransfer';
import { useGetPartnerCurrentSharesCount } from '../hooks/useGetPartnerCurrentSharesCount';

export const AddTradingOfferPageContext =
  createContext<AddTradingOfferPageContextType>(addTradingOfferPageInitValue);

type AddTradingOfferPageProviderProps = {
  children: ReactNode;
  tradingOfferType: TradingOfferType;
  actionType?: string;
};

export const AddTradingOfferPageProvider: FC<
  AddTradingOfferPageProviderProps
> = ({ children, tradingOfferType, actionType = actionTypes.ADD }) => {
  const {
    societyId,
    partnerId: partnerIdParam,
    tradingOfferId,
  } = useParams<{
    societyId: string;
    partnerId: string;
    tradingOfferId: string;
  }>();
  const partnerIdQuery = new URLSearchParams(useLocation().search).get(
    'partnerId'
  );
  const partnerId = partnerIdParam || partnerIdQuery || '';

  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const [selectedPartner, setSelectedPartner] = useState<string>(
    partnerId || ''
  );
  const [sharesToTransfer, setSharesToTransfer] = useState<number>(0);
  const [isSellAllMyShares, setIsSellAllMyShares] = useState<boolean>(false);
  const [isPartialAmountEnabled, setIsPartialAmountEnabled] =
    useState<boolean>(false);
  const [minSharesPerAcceptance, setMinSharesPerAcceptance] =
    useState<number>(1);
  const [additionalNotes, setAdditionalNotes] = useState<string>('');
  const [price, setPrice] = useState<number>(0);
  const [isPricePerShare, setIsPricePerShare] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadedOfferForEdit, setLoadedOfferForEdit] =
    useState<TradingOffer | null>(null);

  const { data: pricesData } = useGetPrices({
    price,
    sharesToTransfer,
    isPricePerShare,
  });

  const { data: partnerOptionsData, actions: partnerOptionsActions } =
    useGetPartnerOptions({
      societyId,
      partnerId,
    });

  const { data: partnerCurrentSharesCountData } =
    useGetPartnerCurrentSharesCount({
      selectedPartnerOption: partnerOptionsData.selectedPartnerOption,
    });

  const { reservedSharesByShareClass, isLoading: isLoadingReservedSellOffers } =
    useGetReservedShares({
      societyId,
      partnerId: selectedPartner,
      excludeTradingOfferId: tradingOfferId,
      tradingOfferType,
    });

  const {
    data: holdingClassOptionsData,
    actions: holdingClassOptionsActions,
    isLoading: isLoadingHoldingClassOptions,
  } = useGetHoldingClassOptions({
    societyId,
    // holdingClassId: draftInvestorToEdit?.holdingClass,
    partnerId: tradingOfferType === 'SELL' ? selectedPartner : undefined,
    reservedSharesByShareClass:
      tradingOfferType === TradingOfferType.SELL
        ? reservedSharesByShareClass
        : undefined,
  });

  const { data: maxSharesToTransferData } = useGetMaxSharesToTransfer({
    partnerActualSharesCount:
      partnerCurrentSharesCountData.partnerCurrentSharesCount,
    selectedHoldingClassOption:
      holdingClassOptionsData.selectedHoldingClassOption,
    tradingOfferType,
  });

  useEffect(() => {
    if (isSellAllMyShares && maxSharesToTransferData.maxSharesToTransfer > 0) {
      setSharesToTransfer(maxSharesToTransferData.maxSharesToTransfer);
    }
  }, [isSellAllMyShares, maxSharesToTransferData.maxSharesToTransfer]);

  useEffect(() => {
    if (
      actionType !== actionTypes.EDIT ||
      !tradingOfferId ||
      !actualSociety?._id
    ) {
      setLoadedOfferForEdit(null);
      return undefined;
    }

    let cancelled = false;

    const loadOffer = async () => {
      setIsLoading(true);
      try {
        const offer = await store.dispatch(getTradingOffer({ tradingOfferId }));
        if (cancelled) {
          return;
        }

        if (
          !offer ||
          String(offer.society) !== String(actualSociety._id) ||
          offer.type !== tradingOfferType
        ) {
          setLoadedOfferForEdit(null);
          errorSwal(t('OfferNotFound'));
          return;
        }

        setLoadedOfferForEdit(offer);

        const pid =
          typeof offer.partner === 'string'
            ? offer.partner
            : offer.partner?._id || '';
        setSelectedPartner(pid || '');
        setSharesToTransfer(offer.sharesCount?.total ?? 0);
        setIsPartialAmountEnabled(Boolean(offer.isPartialAmountEnabled));
        setMinSharesPerAcceptance(offer.minSharesPerAcceptance ?? 1);
        setPrice(offer.sharePrice ?? 0);
        setIsPricePerShare(true);
        setStartDate(formatDateToInput(offer.startDate));
        setEndDate(formatDateToInput(offer.endDate));
      } catch {
        if (!cancelled) {
          setLoadedOfferForEdit(null);
          errorSwal(t('OfferNotFound'));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadOffer();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `t` is recreated every render and causes an infinite fetch loop
  }, [actionType, tradingOfferId, actualSociety?._id, tradingOfferType]);

  useEffect(() => {
    if (!loadedOfferForEdit || actionType !== actionTypes.EDIT) {
      return;
    }
    const opts = holdingClassOptionsData.holdingClassOptions || [];
    if (!opts.length) {
      return;
    }

    const sc = loadedOfferForEdit.shareClass;
    const scId = typeof sc === 'string' ? sc : sc?._id;
    if (!scId) {
      return;
    }

    const opt = opts.find((o) => String(o.value) === String(scId));
    if (opt) {
      holdingClassOptionsActions.handleChangeSelectedHoldingClass(opt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- align select once async options resolve
  }, [
    loadedOfferForEdit,
    holdingClassOptionsData.holdingClassOptions,
    actionType,
  ]);

  const handleChangeSharesToTransfer = (value: number) => {
    setSharesToTransfer(value);
    if (value !== maxSharesToTransferData.maxSharesToTransfer) {
      setIsSellAllMyShares(false);
    }
    if (isPartialAmountEnabled && value > 0) {
      setMinSharesPerAcceptance((prev) => Math.min(prev, value));
    }
  };

  const handleChangeIsSellAllMyShares = () => {
    const newValue = !isSellAllMyShares;
    setIsSellAllMyShares(newValue);
    setSharesToTransfer(
      newValue ? maxSharesToTransferData.maxSharesToTransfer : 0
    );
  };

  const handleChangeIsPartialAmountEnabled = () => {
    setIsPartialAmountEnabled((prev) => !prev);
  };

  const handleChangeMinSharesPerAcceptance = (value: number) => {
    setMinSharesPerAcceptance(value);
  };

  const handleChangeAdditionalNotes = (value: string) => {
    setAdditionalNotes(value);
  };

  const handleChangePrice = (value: number) => {
    setPrice(value);
  };

  const handleChangeIsPricePerShare = () => {
    setIsPricePerShare((prev) => !prev);
  };

  const handleChangeStartDate = (event: ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
  };

  const handleChangeEndDate = (event: ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
  };

  const handleSetDatePreset = (days: number) => {
    const today = new Date();
    const end = new Date();
    end.setDate(today.getDate() + days);

    setStartDate(formatDateToInput(today));
    setEndDate(formatDateToInput(end));
  };

  const handleChangePartner = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    setSelectedPartner(selectedOption ? selectedOption.value : '');
    partnerOptionsActions.handleChangePartnerOption(selectedOption);
    setSharesToTransfer(0);
    setIsSellAllMyShares(false);
  };

  const handleChangeHoldingClass = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    holdingClassOptionsActions.handleChangeSelectedHoldingClass(selectedOption);
    setSharesToTransfer(0);
    setIsSellAllMyShares(false);
  };

  const handleReviewTradingOffer = () => {
    if (!societyId || !selectedPartner) return;
    if (actionType === actionTypes.EDIT && !loadedOfferForEdit) return;

    const tradingOffer: Partial<TradingOffer> & {
      className: string;
      minSharesPerAcceptance?: number;
      additionalNotes?: string;
    } = {
      ...(actionType === actionTypes.EDIT && loadedOfferForEdit
        ? {
            _id: loadedOfferForEdit._id,
            documents: loadedOfferForEdit.documents || [],
            createdAt: loadedOfferForEdit.createdAt,
            updatedAt: loadedOfferForEdit.updatedAt,
          }
        : {}),
      society: societyId,
      partner: selectedPartner,
      shareClass:
        (holdingClassOptionsData.selectedHoldingClassOption?.value as string) ||
        null,
      className:
        holdingClassOptionsData?.selectedHoldingClassOption?.name || '-',
      sharesCount: {
        total: sharesToTransfer,
        agreed: 0,
        approved: 0,
      },
      sharePrice: pricesData.pricePerShare,
      startDate,
      endDate,
      type: tradingOfferType,
      isPartialAmountEnabled,
      minSharesPerAcceptance: isPartialAmountEnabled
        ? minSharesPerAcceptance
        : undefined,
      additionalNotes: additionalNotes || undefined,
      documents: [],
    };

    store.dispatch(
      setModal(
        <PublishTradingOfferModalProvider
          tradingOffer={tradingOffer}
          modalActionType={actionType}
          size="lg"
          publisherPartner={mapPartnerOptionToPartialPartner(
            selectedPartner,
            partnerOptionsData.selectedPartnerOption
          )}
        >
          <PublishTradingOfferModal />
        </PublishTradingOfferModalProvider>
      )
    );
  };

  return (
    <AddTradingOfferPageContext.Provider
      value={{
        data: {
          tradingOfferType,
          actionType,
          partnerOptions: partnerOptionsData.partnerOptions || [],
          selectedPartnerOption: partnerOptionsData.selectedPartnerOption,
          holdingClassOptions:
            holdingClassOptionsData.holdingClassOptions || [],
          selectedHoldingClassOption:
            holdingClassOptionsData.selectedHoldingClassOption,
          partnerCurrentSharesCount:
            partnerCurrentSharesCountData.partnerCurrentSharesCount,
          sharesToTransfer,
          maxSharesToTransfer: maxSharesToTransferData.maxSharesToTransfer,
          isSellAllMyShares,
          isPartialAmountEnabled,
          minSharesPerAcceptance,
          additionalNotes,
          price,
          pricePerShare: pricesData.pricePerShare,
          totalOfferPrice: pricesData.totalOfferPrice,
          societyId: societyId || '',
          societyName: actualSociety?.name || '',
          isPricePerShare,
          startDate,
          endDate,
          isPartnerView: !!partnerId,
          isAdmin,
          isReadOnly: false,
          isLoading: isLoading || !actualSociety,
          isLoadingReservedSellOffers,
          isMarketClosed:
            actualSociety?.configuration?.equityMarket?.status ===
              SocietyEquityMarketStatus.CLOSED ||
            actualSociety?.configuration?.equityMarket?.status ===
              SocietyEquityMarketStatus.UNAVAILABLE,
        },
        actions: {
          handleChangePartner,
          handleSearchPartnerByTerm:
            partnerOptionsActions.handleSearchPartnerByTerm,
          handleChangeHoldingClass,
          handleSearchHoldingClassByTerm:
            holdingClassOptionsActions.handleSearchHoldingClassByTerm,
          handleChangeSharesToTransfer,
          handleChangeIsSellAllMyShares,
          handleChangeIsPartialAmountEnabled,
          handleChangeMinSharesPerAcceptance,
          handleChangeAdditionalNotes,
          handleChangePrice,
          handleChangeIsPricePerShare,
          handleChangeStartDate,
          handleChangeEndDate,
          handleSetDatePreset,
          handleReviewTradingOffer,
        },
      }}
    >
      {children}
    </AddTradingOfferPageContext.Provider>
  );
};

export const useAddTradingOfferPage = () => {
  const context = useContext(AddTradingOfferPageContext);
  if (!context) {
    throw new Error(
      'useAddTradingOfferPage must be used within a AddTradingOfferPageProvider'
    );
  }
  return context;
};
